import { useEffect, useMemo, useRef, useState } from "react";
import { Copy, Image as ImageIcon, Loader2, Pencil, Plus, Save, Trash2, Upload, X } from "lucide-react";
import { api, formatErr, resolveMediaUrl } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { toast } from "sonner";

const tabs = ["settings", "home", "products", "solutions", "industries", "blogs", "testimonials", "faqs", "partners", "downloads"];

const collectionLabels = {
  products: "Products",
  solutions: "Solutions",
  industries: "Industries",
  blogs: "Blogs",
  testimonials: "Testimonials",
  faqs: "FAQs",
  partners: "Partners",
  downloads: "Downloads",
};

const editorSchemas = {
  products: [
    ["title", "Product name", "text"],
    ["slug", "SEO URL slug", "text"],
    ["category", "Category", "productCategory"],
    ["subcategory", "Subcategory / series", "text"],
    ["summary", "Short description", "textarea"],
    ["image", "Main product image", "image"],
    ["galleryImages", "Gallery image URLs", "lines"],
    ["lifestyleImage", "Lifestyle / installation image", "image"],
    ["videoUrl", "Product video embed URL", "text"],
    ["modelNumber", "Model number", "text"],
    ["priceText", "Price / CTA text", "text"],
    ["availabilityStatus", "Availability status", "text"],
    ["badges", "Badges (e.g. New, In Stock)", "lines"],
    ["features", "Card highlights (3-5 short points)", "lines"],
    ["keyFeatures", "Key features (detail page)", "titleDescList"],
    ["technicalSpecifications", "Legacy specification lines (fallback only)", "lines"],
    ["specGroups", "Technical specifications (grouped)", "specGroups"],
    ["variants", "Available models / variants", "variants"],
    ["applications", "Applications", "titleDescList"],
    ["industriesServed", "Industries served", "lines"],
    ["installationSteps", "Installation / deployment steps", "titleDescList"],
    ["downloads", "Downloads (datasheet, manual, etc.)", "downloadList"],
    ["faqs", "Product FAQs", "qaList"],
    ["warrantyInformation", "Warranty information", "textarea"],
    ["certifications", "Certifications", "lines"],
    ["description", "Detailed description", "textarea"],
    ["whatsappMessageTemplate", "Custom WhatsApp message (optional)", "textarea"],
    ["seoTitle", "SEO title", "text"],
    ["seoDescription", "Meta description", "textarea"],
    ["seoKeywords", "Focus / secondary keywords", "text"],
    ["canonicalUrl", "Canonical URL (optional)", "text"],
    ["robotsIndex", "Allow search engines to index this page", "checkbox"],
    ["brochureDownloadMode", "Brochure download", "brochureMode"],
    ["brochureTemplate", "Brochure layout", "brochureTemplate"],
    ["sort", "Display order", "number"],
    ["active", "Visible on website", "checkbox"],
  ],
  solutions: [
    ["title", "Solution title", "text"],
    ["slug", "SEO URL slug", "text"],
    ["category", "Category", "text"],
    ["summary", "Short description", "textarea"],
    ["image", "Hero / card image URL", "image"],
    ["features", "Features", "lines"],
    ["benefits", "Benefits", "lines"],
    ["description", "Detailed content", "textarea"],
    ["sort", "Display order", "number"],
    ["active", "Visible on website", "checkbox"],
  ],
  industries: [
    ["title", "Industry title", "text"],
    ["slug", "SEO URL slug", "text"],
    ["summary", "Short description", "textarea"],
    ["image", "Industry image URL", "image"],
    ["features", "Use cases", "lines"],
    ["benefits", "Benefits", "lines"],
    ["description", "Detailed content", "textarea"],
    ["sort", "Display order", "number"],
    ["active", "Visible on website", "checkbox"],
  ],
  blogs: [
    ["title", "Blog title", "text"],
    ["slug", "SEO URL slug", "text"],
    ["category", "Category", "text"],
    ["summary", "Excerpt", "textarea"],
    ["image", "Featured image URL", "image"],
    ["author", "Author", "text"],
    ["publishedAt", "Published date", "date"],
    ["content", "Article content", "textarea"],
    ["sort", "Display order", "number"],
    ["active", "Published", "checkbox"],
  ],
  testimonials: [
    ["name", "Client name", "text"],
    ["business", "Business name", "text"],
    ["industry", "Industry", "text"],
    ["review", "Review", "textarea"],
    ["rating", "Rating", "number"],
    ["image", "Photo / logo URL", "image"],
    ["sort", "Display order", "number"],
    ["active", "Visible on website", "checkbox"],
  ],
  faqs: [
    ["question", "Question", "text"],
    ["answer", "Answer", "textarea"],
    ["category", "Category", "text"],
    ["sort", "Display order", "number"],
    ["active", "Visible on website", "checkbox"],
  ],
  partners: [
    ["name", "Partner / brand name", "text"],
    ["logo", "Logo URL", "image"],
    ["website", "Website", "text"],
    ["sort", "Display order", "number"],
    ["active", "Visible on website", "checkbox"],
  ],
  downloads: [
    ["title", "Download title", "text"],
    ["category", "Category", "text"],
    ["url", "File URL", "text"],
    ["summary", "Description", "textarea"],
    ["image", "Cover image URL", "image"],
    ["sort", "Display order", "number"],
    ["active", "Visible on website", "checkbox"],
  ],
};

export default function AdminWebsite() {
  const [cms, setCms] = useState(null);
  const [tab, setTab] = useState("settings");
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/admin/website").then(({ data }) => setCms(data)).catch((e) => toast.error(formatErr(e.response?.data?.detail)));
  }, []);

  useEffect(() => {
    setSelectedIndex(0);
  }, [tab]);

  const setNested = (section, key, value) => {
    setCms((current) => ({ ...current, [section]: { ...(current?.[section] || {}), [key]: value } }));
  };

  const setBrochure = (key, value) => {
    setCms((current) => ({
      ...current,
      settings: {
        ...(current?.settings || {}),
        brochure: {
          ...((current?.settings || {}).brochure || {}),
          [key]: value,
        },
      },
    }));
  };

  const save = async () => {
    setBusy(true);
    try {
      const { data } = await api.put("/admin/website", cms);
      setCms(data);
      toast.success("Website CMS saved");
    } catch (e) {
      toast.error(formatErr(e.response?.data?.detail || e.message));
    } finally {
      setBusy(false);
    }
  };

  if (!cms) return <div className="p-8 text-sm text-slate-500">Loading website CMS...</div>;

  return (
    <main className="mx-auto max-w-7xl px-4 py-8">
      <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
        <div>
          <div className="text-xs font-black uppercase tracking-[0.22em] text-slate-500">Admin / Website CMS</div>
          <h1 className="font-display text-4xl font-black tracking-tight text-slate-950">Website content.</h1>
          <p className="mt-2 text-sm text-slate-500">Manage public RP Infotech pages without changing code.</p>
        </div>
        <Button onClick={save} disabled={busy} className="rounded-sm bg-slate-950"><Save className="mr-2 h-4 w-4" />Save CMS</Button>
      </div>

      <div className="mb-6 flex flex-wrap gap-2 border border-slate-200 bg-slate-100 p-2">
        {tabs.map((item) => (
          <button key={item} onClick={() => setTab(item)} className={`px-4 py-2 text-sm font-black capitalize ${tab === item ? "bg-white text-slate-950 shadow-sm" : "text-slate-600"}`}>{item}</button>
        ))}
      </div>

      {tab === "settings" ? (
        <EditorCard title="Website Settings">
          <Field label="Brand name" value={cms.settings?.brandName} onChange={(v) => setNested("settings", "brandName", v)} />
          <Field label="Tagline" value={cms.settings?.tagline} onChange={(v) => setNested("settings", "tagline", v)} />
          <ImageField label="Logo URL" value={cms.settings?.logo} onChange={(v) => setNested("settings", "logo", v)} />
          <Field label="Phone" value={cms.settings?.phone} onChange={(v) => setNested("settings", "phone", v)} />
          <Field label="WhatsApp number" value={cms.settings?.whatsapp} onChange={(v) => setNested("settings", "whatsapp", v)} />
          <Field label="Email" value={cms.settings?.email} onChange={(v) => setNested("settings", "email", v)} />
          <Field label="Address" value={cms.settings?.address} onChange={(v) => setNested("settings", "address", v)} />
          <TextField label="Footer content" value={cms.settings?.footer} onChange={(v) => setNested("settings", "footer", v)} wide />
          <div className="md:col-span-2 border-t border-slate-200 pt-5">
            <h3 className="text-lg font-black">Brochure Branding</h3>
            <p className="mt-1 text-sm text-slate-500">Used for dynamic dealer brochure PDFs.</p>
          </div>
          <ImageField label="RP brochure logo" value={cms.settings?.brochure?.rpLogo || cms.settings?.logo} onChange={(v) => setBrochure("rpLogo", v)} />
          <Field label="Brochure website" value={cms.settings?.brochure?.website} onChange={(v) => setBrochure("website", v)} />
          <Field label="Brochure contact number" value={cms.settings?.brochure?.contactNumber} onChange={(v) => setBrochure("contactNumber", v)} />
          <Field label="Brochure email" value={cms.settings?.brochure?.email} onChange={(v) => setBrochure("email", v)} />
          <Field label="Primary brand color" value={cms.settings?.brochure?.primaryColor} onChange={(v) => setBrochure("primaryColor", v)} />
          <Field label="Accent brand color" value={cms.settings?.brochure?.accentColor} onChange={(v) => setBrochure("accentColor", v)} />
          <Field label="Watermark text" value={cms.settings?.brochure?.watermark} onChange={(v) => setBrochure("watermark", v)} />
          <Field label="Footer text" value={cms.settings?.brochure?.footerText} onChange={(v) => setBrochure("footerText", v)} />
          <TextField label="Brochure company address" value={cms.settings?.brochure?.companyAddress} onChange={(v) => setBrochure("companyAddress", v)} wide />
        </EditorCard>
      ) : tab === "home" ? (
        <EditorCard title="Homepage Content">
          <Field label="Hero title" value={cms.home?.heroTitle} onChange={(v) => setNested("home", "heroTitle", v)} />
          <ImageField label="Hero banner image URL" value={cms.home?.heroImage} onChange={(v) => setNested("home", "heroImage", v)} />
          <TextField label="Hero subtitle" value={cms.home?.heroSubtitle} onChange={(v) => setNested("home", "heroSubtitle", v)} wide />
          <Field label="Primary CTA" value={cms.home?.primaryCta} onChange={(v) => setNested("home", "primaryCta", v)} />
          <Field label="Secondary CTA" value={cms.home?.secondaryCta} onChange={(v) => setNested("home", "secondaryCta", v)} />
          <Field label="Third CTA" value={cms.home?.thirdCta} onChange={(v) => setNested("home", "thirdCta", v)} />
          <StatsEditor rows={cms.home?.stats || []} onChange={(rows) => setNested("home", "stats", rows)} />
        </EditorCard>
      ) : (
        <CollectionEditor
          cms={cms}
          tab={tab}
          selectedIndex={selectedIndex}
          setSelectedIndex={setSelectedIndex}
          setCms={setCms}
        />
      )}
    </main>
  );
}

function CollectionEditor({ cms, tab, selectedIndex, setSelectedIndex, setCms }) {
  const rows = useMemo(() => Array.isArray(cms[tab]) ? cms[tab] : [], [cms, tab]);
  const schema = editorSchemas[tab] || [];
  const [productCategoryFilter, setProductCategoryFilter] = useState("All Products");
  const [newProductCategory, setNewProductCategory] = useState("");
  const [editingProductCategory, setEditingProductCategory] = useState("");
  const [editingProductCategoryName, setEditingProductCategoryName] = useState("");
  const productCategories = useMemo(() => {
    const names = [];
    for (const cat of cms.productCategories || []) {
      if (cat?.name) names.push(String(cat.name).trim());
    }
    for (const row of rows) {
      if (tab === "products" && row?.category) names.push(String(row.category).trim());
    }
    return [...new Set(names.filter(Boolean))];
  }, [cms.productCategories, rows, tab]);
  const visibleRows = useMemo(() => {
    if (tab !== "products" || productCategoryFilter === "All Products") {
      return rows.map((row, index) => ({ row, index }));
    }
    return rows
      .map((row, index) => ({ row, index }))
      .filter(({ row }) => row.category === productCategoryFilter && slugify(row.title) !== slugify(productCategoryFilter));
  }, [productCategoryFilter, rows, tab]);
  const selectedItemIsVisible = tab !== "products" || productCategoryFilter === "All Products" || visibleRows.some(({ index }) => index === selectedIndex);
  const item = selectedItemIsVisible ? rows[selectedIndex] || null : null;
  const selectedProductCategory = useMemo(() => {
    if (tab !== "products" || productCategoryFilter === "All Products") return null;
    return (cms.productCategories || []).find((cat) => cat?.name === productCategoryFilter) || {
      id: slugify(productCategoryFilter),
      slug: slugify(productCategoryFilter),
      name: productCategoryFilter,
      summary: "",
      description: "",
      image: "",
      features: [],
      sort: productCategories.indexOf(productCategoryFilter) + 1,
      active: true,
    };
  }, [cms.productCategories, productCategories, productCategoryFilter, tab]);

  const updateRows = (nextRows, nextIndex = selectedIndex) => {
    setCms((current) => ({ ...current, [tab]: nextRows }));
    setSelectedIndex(Math.max(0, Math.min(nextIndex, nextRows.length - 1)));
  };

  const addItem = () => {
    const next = makeEmptyItem(tab, rows.length + 1);
    if (tab === "products" && productCategoryFilter !== "All Products") next.category = productCategoryFilter;
    updateRows([...rows, next], rows.length);
  };

  const duplicateItem = () => {
    if (!item) return;
    const next = { ...item, title: `${item.title || item.name || "Item"} copy`, name: item.name ? `${item.name} copy` : item.name, slug: `${item.slug || slugify(item.title || item.name || "item")}-copy`, sort: rows.length + 1 };
    updateRows([...rows, next], rows.length);
  };

  const removeItem = () => {
    if (!item) return;
    const nextRows = rows.filter((_, index) => index !== selectedIndex);
    updateRows(nextRows, selectedIndex - 1);
  };

  const setItemField = (key, value) => {
    const nextRows = rows.map((row, index) => index === selectedIndex ? { ...row, [key]: value } : row);
    updateRows(nextRows, selectedIndex);
  };

  const saveProductCategory = () => {
    const name = newProductCategory.trim();
    if (!name) return;
    if (productCategories.some((cat) => cat.toLowerCase() === name.toLowerCase())) {
      toast.error("Category already exists");
      return;
    }
    setCms((current) => ({
      ...current,
      productCategories: [...(current.productCategories || []), { id: slugify(name), slug: slugify(name), name, summary: "", description: "", image: "", features: [], sort: (current.productCategories || []).length + 1, active: true }],
    }));
    setProductCategoryFilter(name);
    setSelectedIndex(-1);
    setNewProductCategory("");
  };

  const deleteProductCategory = (category) => {
    if (!window.confirm(`Remove category "${category}"? Products in it will become uncategorized.`)) return;
    setCms((current) => ({
      ...current,
      productCategories: (current.productCategories || []).filter((cat) => cat.name !== category),
      products: (current.products || []).map((row) => row.category === category ? { ...row, category: "" } : row),
    }));
    if (productCategoryFilter === category) setProductCategoryFilter("All Products");
  };

  const startEditProductCategory = (category) => {
    setEditingProductCategory(category);
    setEditingProductCategoryName(category);
  };

  const cancelEditProductCategory = () => {
    setEditingProductCategory("");
    setEditingProductCategoryName("");
  };

  const updateProductCategory = () => {
    const oldName = editingProductCategory;
    const newName = editingProductCategoryName.trim();
    if (!oldName || !newName) return;
    if (oldName.toLowerCase() !== newName.toLowerCase() && productCategories.some((cat) => cat.toLowerCase() === newName.toLowerCase())) {
      toast.error("Category already exists");
      return;
    }
    setCms((current) => ({
      ...current,
      productCategories: (current.productCategories || []).map((cat) => cat.name === oldName ? { ...cat, id: slugify(newName), slug: cat.slug || slugify(newName), name: newName } : cat),
      products: (current.products || []).map((row) => row.category === oldName ? { ...row, category: newName } : row),
    }));
    if (productCategoryFilter === oldName) setProductCategoryFilter(newName);
    cancelEditProductCategory();
  };

  const setProductCategoryField = (key, value) => {
    if (!selectedProductCategory) return;
    const oldName = selectedProductCategory.name;
    const nextName = key === "name" ? String(value || "").trimStart() : oldName;
    const safeName = nextName.trim() || oldName;
    if (key === "name" && safeName.toLowerCase() !== oldName.toLowerCase() && productCategories.some((cat) => cat.toLowerCase() === safeName.toLowerCase())) {
      toast.error("Category already exists");
      return;
    }
    setCms((current) => {
      const currentCategories = current.productCategories || [];
      const existingIndex = currentCategories.findIndex((cat) => cat?.name === oldName);
      const baseCategory = existingIndex >= 0 ? currentCategories[existingIndex] : selectedProductCategory;
      const nextCategory = {
        ...baseCategory,
        id: key === "name" ? slugify(safeName) : baseCategory.id || slugify(oldName),
        slug: key === "name" ? (baseCategory.slug || slugify(safeName)) : (baseCategory.slug || slugify(oldName)),
        [key]: key === "name" ? safeName : value,
      };
      const nextCategories = existingIndex >= 0
        ? currentCategories.map((cat, index) => index === existingIndex ? nextCategory : cat)
        : [...currentCategories, nextCategory];
      return {
        ...current,
        productCategories: nextCategories,
        products: key === "name"
          ? (current.products || []).map((row) => row.category === oldName ? { ...row, category: safeName } : row)
          : current.products,
      };
    });
    if (key === "name") setProductCategoryFilter(safeName);
  };

  const copyWebsiteProductToCatalogue = async () => {
    if (!item?.slug) return;
    try {
      await api.post(`/admin/website/products/${encodeURIComponent(item.slug)}/copy-to-catalogue`);
      toast.success("Product copied to E-Catalogue");
    } catch (e) {
      toast.error(formatErr(e.response?.data?.detail || e.message));
    }
  };

  const selectProductCategory = (category) => {
    setProductCategoryFilter(category);
    setSelectedIndex(category === "All Products" ? 0 : -1);
  };

  return (
    <section className="grid gap-5 lg:grid-cols-[330px_1fr]">
      <aside className="h-max border border-slate-200 bg-white p-4 shadow-sm">
        {tab === "products" && (
          <div className="mb-4 border-b border-slate-200 pb-4">
            <div className="mb-2 text-xs font-black uppercase tracking-[0.18em] text-slate-500">Product Categories</div>
            <div className="grid gap-2">
              {["All Products", ...productCategories].map((category) => (
                <div key={category} className={category === "All Products" ? "grid gap-1" : "grid grid-cols-[1fr_auto_auto] gap-1"}>
                  {editingProductCategory === category ? (
                    <>
                      <Input value={editingProductCategoryName} onChange={(e) => setEditingProductCategoryName(e.target.value)} className="h-9" />
                      <button type="button" className="h-9 w-9 rounded-sm border border-green-200 text-green-700" title="Save category" onClick={updateProductCategory}>
                        <Save className="mx-auto h-4 w-4" />
                      </button>
                      <button type="button" className="h-9 w-9 rounded-sm border border-slate-200 text-slate-600" title="Cancel edit" onClick={cancelEditProductCategory}>
                        <X className="mx-auto h-4 w-4" />
                      </button>
                    </>
                  ) : (
                    <>
                      <button
                        type="button"
                        onClick={() => selectProductCategory(category)}
                        className={`rounded-sm px-3 py-2 text-left text-sm font-black ${productCategoryFilter === category ? "bg-blue-600 text-white" : "bg-slate-50 text-slate-700 hover:bg-blue-50"}`}
                      >
                        {category}
                      </button>
                      {category !== "All Products" && (
                        <>
                          <button type="button" className="h-9 w-9 rounded-sm border border-slate-200 text-slate-600" title="Edit category" onClick={() => startEditProductCategory(category)}>
                            <Pencil className="mx-auto h-4 w-4" />
                          </button>
                          <button type="button" className="h-9 w-9 rounded-sm border border-red-200 text-red-600" title="Delete category" onClick={() => deleteProductCategory(category)}>
                            <Trash2 className="mx-auto h-4 w-4" />
                          </button>
                        </>
                      )}
                    </>
                  )}
                </div>
              ))}
            </div>
            <div className="mt-3 flex gap-2">
              <Input value={newProductCategory} onChange={(e) => setNewProductCategory(e.target.value)} placeholder="New category" />
              <Button type="button" size="sm" className="rounded-sm bg-slate-950" onClick={saveProductCategory}>Add</Button>
            </div>
          </div>
        )}
        <div className="mb-4 flex items-center justify-between gap-2">
          <div>
            <div className="text-xs font-black uppercase tracking-[0.18em] text-slate-500">{collectionLabels[tab]}</div>
            <h2 className="text-xl font-black">Content list</h2>
          </div>
          <Button onClick={addItem} size="sm" className="rounded-sm bg-slate-950"><Plus className="mr-1 h-4 w-4" />Add</Button>
        </div>
        <div className="grid max-h-[680px] gap-2 overflow-auto pr-1">
          {rows.length === 0 ? (
            <button onClick={addItem} className="border border-dashed border-slate-300 p-5 text-left text-sm font-bold text-slate-500">No items yet. Add first item.</button>
          ) : visibleRows.map(({ row, index }) => (
            <button
              key={`${row.slug || row.title || row.name || index}-${index}`}
              onClick={() => setSelectedIndex(index)}
              className={`border p-3 text-left transition ${index === selectedIndex ? "border-slate-950 bg-slate-950 text-white" : "border-slate-200 bg-white hover:border-slate-400"}`}
            >
              <div className="font-black">{row.title || row.name || row.question || `Item ${index + 1}`}</div>
              <div className={`mt-1 text-xs ${index === selectedIndex ? "text-slate-300" : "text-slate-500"}`}>{row.category || row.industry || row.slug || (row.active === false ? "Hidden" : "Visible")}</div>
            </button>
          ))}
        </div>
      </aside>

      <EditorCard title={item ? `Edit ${collectionLabels[tab].slice(0, -1)}` : selectedProductCategory ? `Category Details: ${selectedProductCategory.name}` : `Add ${collectionLabels[tab]}`}>
        {selectedProductCategory && !item ? (
          <ProductCategoryDetailEditor
            category={selectedProductCategory}
            onChange={setProductCategoryField}
            onAddProduct={addItem}
          />
        ) : !item ? (
          <div className="md:col-span-2 rounded-sm border border-dashed border-slate-300 bg-slate-50 p-6">
            <p className="text-sm font-semibold text-slate-600">
              Select an item from the list or add a new one.
            </p>
            <Button onClick={addItem} className="mt-4 rounded-sm bg-slate-950"><Plus className="mr-2 h-4 w-4" />Add item</Button>
          </div>
        ) : (
          <>
            {schema.map(([key, label, type]) => (
              <SchemaField key={key} item={item} fieldKey={key} label={label} type={type} productCategories={productCategories} onChange={(value) => setItemField(key, value)} setField={setItemField} />
            ))}
            <div className="md:col-span-2 flex flex-wrap gap-2 border-t border-slate-200 pt-4">
              <Button type="button" variant="outline" className="rounded-sm" onClick={duplicateItem}><Copy className="mr-2 h-4 w-4" />Duplicate</Button>
              {tab === "products" && <Button type="button" variant="outline" className="rounded-sm" onClick={copyWebsiteProductToCatalogue}><Copy className="mr-2 h-4 w-4" />Copy to E-Catalogue</Button>}
              <Button type="button" variant="outline" className="rounded-sm border-red-200 text-red-600 hover:text-red-700" onClick={removeItem}><Trash2 className="mr-2 h-4 w-4" />Delete</Button>
            </div>
          </>
        )}
      </EditorCard>
    </section>
  );
}

function ProductCategoryDetailEditor({ category, onChange, onAddProduct }) {
  return (
    <>
      <Field label="Category name" value={category.name} onChange={(value) => onChange("name", value)} />
      <Field label="SEO URL slug" value={category.slug || slugify(category.name)} onChange={(value) => onChange("slug", slugify(value))} />
      <TextField label="Short description" value={category.summary} onChange={(value) => onChange("summary", value)} wide />
      <ImageField label="Category hero image" value={category.image} onChange={(value) => onChange("image", value)} />
      <ImageField label="Mobile hero image (optional)" value={category.mobileHeroImage} onChange={(value) => onChange("mobileHeroImage", value)} />
      <Field label="Hero title (overrides category name)" value={category.heroTitle} onChange={(value) => onChange("heroTitle", value)} />
      <Field label="Hero subtitle" value={category.heroSubtitle} onChange={(value) => onChange("heroSubtitle", value)} />
      <Field label="Category type (software / hardware / service)" value={category.categoryType} onChange={(value) => onChange("categoryType", value)} />
      <Field label="Display order" type="number" value={category.sort} onChange={(value) => onChange("sort", value)} />
      <LineListField label="Category highlights (cards)" value={category.features} onChange={(value) => onChange("features", value)} wide />
      <LineListField label="Benefits" value={category.benefits} onChange={(value) => onChange("benefits", value)} wide />
      <LineListField label="Key technologies" value={category.technologies} onChange={(value) => onChange("technologies", value)} wide />
      <LineListField label="Industries served" value={category.industriesServed} onChange={(value) => onChange("industriesServed", value)} wide />
      <LineListField label="Why choose RP Infotech" value={category.whyChooseUs} onChange={(value) => onChange("whyChooseUs", value)} wide />
      <TitleDescriptionListField label="Applications" value={category.applications} onChange={(value) => onChange("applications", value)} />
      <TitleDescriptionListField label="Installation process" value={category.installationProcess} onChange={(value) => onChange("installationProcess", value)} />
      <QuestionAnswerListField label="Category FAQs" value={category.faqs} onChange={(value) => onChange("faqs", value)} />
      <TextField label="Detailed category content" value={category.description} onChange={(value) => onChange("description", value)} wide />
      <Field label="SEO title" value={category.seoTitle} onChange={(value) => onChange("seoTitle", value)} />
      <TextField label="Meta description" value={category.seoDescription} onChange={(value) => onChange("seoDescription", value)} />
      <Field label="Catalogue download URL (optional)" value={category.catalogueUrl} onChange={(value) => onChange("catalogueUrl", value)} />
      <CheckboxField label="Visible on website" value={category.active !== false} onChange={(value) => onChange("active", value)} />
      <div className="flex items-end">
        <Button onClick={onAddProduct} className="rounded-sm bg-slate-950"><Plus className="mr-2 h-4 w-4" />Add Product in This Category</Button>
      </div>
    </>
  );
}

function SchemaField({ item, fieldKey, label, type, productCategories = [], onChange, setField }) {
  if (type === "textarea") return <TextField label={label} value={item[fieldKey]} onChange={onChange} wide />;
  if (type === "lines") return <LineListField label={label} value={item[fieldKey]} onChange={onChange} wide />;
  if (type === "image") return <ImageField label={label} value={item[fieldKey]} onChange={onChange} />;
  if (type === "checkbox") return <CheckboxField label={label} value={item[fieldKey] !== false} onChange={onChange} />;
  if (type === "productCategory") return <ProductCategoryField label={label} value={item[fieldKey]} categories={productCategories} onChange={onChange} />;
  if (type === "titleDescList") return <TitleDescriptionListField label={label} value={item[fieldKey]} onChange={onChange} />;
  if (type === "qaList") return <QuestionAnswerListField label={label} value={item[fieldKey]} onChange={onChange} />;
  if (type === "downloadList") return <LabelUrlListField label={label} value={item[fieldKey]} onChange={onChange} />;
  if (type === "specGroups") return <SpecGroupsField label={label} value={item[fieldKey]} onChange={onChange} />;
  if (type === "variants") return <VariantsField label={label} value={item[fieldKey]} onChange={onChange} columnsValue={item.variantColumns} columnsOnChange={(cols) => setField && setField("variantColumns", cols)} />;
  if (type === "brochureMode") {
    return (
      <SelectField
        label={label}
        value={item[fieldKey] || "dealer_only"}
        onChange={onChange}
        options={[
          ["public", "Public Download"],
          ["dealer_only", "Dealer Only Download"],
          ["private", "Private (No Download)"],
        ]}
      />
    );
  }
  if (type === "brochureTemplate") {
    return (
      <SelectField
        label={label}
        value={item[fieldKey] || "single"}
        onChange={onChange}
        options={[
          ["single", "Single Page"],
          ["two_page", "Two Page"],
          ["catalogue", "Multi-Page Catalogue Style"],
        ]}
      />
    );
  }
  return <Field label={label} type={type} value={item[fieldKey]} onChange={onChange} />;
}

function ProductCategoryField({ label, value, categories, onChange }) {
  return (
    <div>
      <Label>{label}</Label>
      <select className="mt-2 h-10 w-full border border-slate-300 bg-white px-3 text-sm" value={value || ""} onChange={(e) => onChange(e.target.value)}>
        <option value="">Uncategorized</option>
        {categories.map((category) => <option key={category} value={category}>{category}</option>)}
      </select>
      <p className="mt-1 text-xs text-slate-500">Add categories from the left panel, then select one here.</p>
    </div>
  );
}

function EditorCard({ title, children }) {
  return <section className="border border-slate-200 bg-white p-5 shadow-sm"><h2 className="mb-5 text-xl font-black">{title}</h2><div className="grid gap-4 md:grid-cols-2">{children}</div></section>;
}

function Field({ label, value, onChange, type = "text" }) {
  return <div><Label>{label}</Label><Input type={type} className="mt-2" value={value ?? ""} onChange={(e) => onChange(type === "number" ? Number(e.target.value || 0) : e.target.value)} /></div>;
}

function SelectField({ label, value, onChange, options }) {
  return (
    <div>
      <Label>{label}</Label>
      <select className="mt-2 h-10 w-full border border-slate-300 bg-white px-3 text-sm" value={value || ""} onChange={(e) => onChange(e.target.value)}>
        {options.map(([optionValue, optionLabel]) => <option key={optionValue} value={optionValue}>{optionLabel}</option>)}
      </select>
    </div>
  );
}

function TextField({ label, value, onChange, wide }) {
  return <div className={wide ? "md:col-span-2" : ""}><Label>{label}</Label><textarea className="mt-2 min-h-28 w-full border border-slate-300 p-3 text-sm" value={value || ""} onChange={(e) => onChange(e.target.value)} /></div>;
}

function LineListField({ label, value, onChange, wide }) {
  const text = Array.isArray(value) ? value.join("\n") : "";
  return (
    <div className={wide ? "md:col-span-2" : ""}>
      <Label>{label}</Label>
      <textarea
        className="mt-2 min-h-32 w-full border border-slate-300 p-3 text-sm"
        value={text}
        placeholder="One item per line"
        onChange={(e) => onChange(e.target.value.split("\n").map((line) => line.trim()).filter(Boolean))}
      />
    </div>
  );
}

function ImageField({ label, value, onChange }) {
  const fileRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const previewUrl = resolveMediaUrl(value);

  const uploadImage = async (file) => {
    if (!file) return;
    if (!["image/jpeg", "image/png", "image/webp", "image/gif"].includes(file.type)) {
      toast.error("Please upload JPG, PNG, WEBP or GIF image only");
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("name", file.name);
      fd.append("folder", "website-cms");
      const { data } = await api.post("/admin/seo/media", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const uploadedUrl = resolveMediaUrl(data.public_url || data.url || (data.id ? `/api/media/serve/${data.id}` : ""));
      onChange(uploadedUrl);
      toast.success("Image uploaded");
    } catch (e) {
      toast.error(formatErr(e.response?.data?.detail || e.message || e));
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  return (
    <div>
      <Label>{label}</Label>
      <div className="mt-2 flex gap-3">
        <div className="grid h-20 w-24 place-items-center overflow-hidden border border-slate-200 bg-slate-50">
          {previewUrl ? <img src={previewUrl} alt="" className="h-full w-full object-cover" /> : <ImageIcon className="h-5 w-5 text-slate-400" />}
        </div>
        <div className="min-w-0 flex-1 space-y-2">
          <Input value={value || ""} placeholder="/image.png or https://..." onChange={(e) => onChange(e.target.value)} />
          <div className="flex flex-wrap items-center gap-2">
            <input
              ref={fileRef}
              type="file"
              accept="image/jpeg,image/png,image/webp,image/gif"
              className="hidden"
              onChange={(e) => uploadImage(e.target.files?.[0])}
            />
            <Button type="button" variant="outline" size="sm" className="rounded-sm" disabled={uploading} onClick={() => fileRef.current?.click()}>
              {uploading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Upload className="mr-2 h-4 w-4" />}
              {uploading ? "Uploading..." : "Upload image"}
            </Button>
            <span className="text-xs text-slate-500">or paste image URL above</span>
          </div>
        </div>
      </div>
    </div>
  );
}

function CheckboxField({ label, value, onChange }) {
  return (
    <label className="flex items-center gap-3 border border-slate-200 p-3 text-sm font-black">
      <input type="checkbox" checked={Boolean(value)} onChange={(e) => onChange(e.target.checked)} />
      {label}
    </label>
  );
}

function TitleDescriptionListField({ label, value, onChange, titleLabel = "Title", descriptionLabel = "Description" }) {
  const rows = Array.isArray(value) ? value : [];
  const update = (index, key, next) => onChange(rows.map((row, idx) => (idx === index ? { ...row, [key]: next } : row)));
  const add = () => onChange([...rows, { title: "", description: "" }]);
  const remove = (index) => onChange(rows.filter((_, idx) => idx !== index));
  return (
    <div className="md:col-span-2">
      <div className="mb-2 flex items-center justify-between">
        <Label>{label}</Label>
        <Button type="button" size="sm" variant="outline" onClick={add} className="rounded-sm"><Plus className="mr-1 h-4 w-4" />Add</Button>
      </div>
      <div className="grid gap-2">
        {rows.map((row, index) => (
          <div key={index} className="grid gap-2 border border-slate-200 p-3 md:grid-cols-[1fr_2fr_auto]">
            <Input placeholder={titleLabel} value={row.title || ""} onChange={(e) => update(index, "title", e.target.value)} />
            <Input placeholder={descriptionLabel} value={row.description || ""} onChange={(e) => update(index, "description", e.target.value)} />
            <Button type="button" variant="outline" className="rounded-sm border-red-200 text-red-600" onClick={() => remove(index)}><Trash2 className="h-4 w-4" /></Button>
          </div>
        ))}
        {!rows.length && <p className="text-xs text-slate-500">No entries yet.</p>}
      </div>
    </div>
  );
}

function QuestionAnswerListField({ label, value, onChange }) {
  const rows = Array.isArray(value) ? value : [];
  const update = (index, key, next) => onChange(rows.map((row, idx) => (idx === index ? { ...row, [key]: next } : row)));
  const add = () => onChange([...rows, { question: "", answer: "" }]);
  const remove = (index) => onChange(rows.filter((_, idx) => idx !== index));
  return (
    <div className="md:col-span-2">
      <div className="mb-2 flex items-center justify-between">
        <Label>{label}</Label>
        <Button type="button" size="sm" variant="outline" onClick={add} className="rounded-sm"><Plus className="mr-1 h-4 w-4" />Add</Button>
      </div>
      <div className="grid gap-2">
        {rows.map((row, index) => (
          <div key={index} className="grid gap-2 border border-slate-200 p-3">
            <Input placeholder="Question" value={row.question || ""} onChange={(e) => update(index, "question", e.target.value)} />
            <textarea className="min-h-20 w-full border border-slate-300 p-2 text-sm" placeholder="Answer" value={row.answer || ""} onChange={(e) => update(index, "answer", e.target.value)} />
            <div><Button type="button" size="sm" variant="outline" className="rounded-sm border-red-200 text-red-600" onClick={() => remove(index)}><Trash2 className="mr-1 h-4 w-4" />Remove</Button></div>
          </div>
        ))}
        {!rows.length && <p className="text-xs text-slate-500">No FAQs yet.</p>}
      </div>
    </div>
  );
}

function LabelUrlListField({ label, value, onChange }) {
  const rows = Array.isArray(value) ? value : [];
  const update = (index, key, next) => onChange(rows.map((row, idx) => (idx === index ? { ...row, [key]: next } : row)));
  const add = () => onChange([...rows, { label: "", url: "" }]);
  const remove = (index) => onChange(rows.filter((_, idx) => idx !== index));
  return (
    <div className="md:col-span-2">
      <div className="mb-2 flex items-center justify-between">
        <Label>{label}</Label>
        <Button type="button" size="sm" variant="outline" onClick={add} className="rounded-sm"><Plus className="mr-1 h-4 w-4" />Add</Button>
      </div>
      <div className="grid gap-2">
        {rows.map((row, index) => (
          <div key={index} className="grid gap-2 md:grid-cols-[1fr_2fr_auto]">
            <Input placeholder="Label (e.g. User Manual)" value={row.label || ""} onChange={(e) => update(index, "label", e.target.value)} />
            <Input placeholder="File URL" value={row.url || ""} onChange={(e) => update(index, "url", e.target.value)} />
            <Button type="button" variant="outline" className="rounded-sm border-red-200 text-red-600" onClick={() => remove(index)}><Trash2 className="h-4 w-4" /></Button>
          </div>
        ))}
        {!rows.length && <p className="text-xs text-slate-500">No downloads added yet.</p>}
      </div>
    </div>
  );
}

function SpecGroupsField({ label, value, onChange }) {
  const groups = Array.isArray(value) ? value : [];
  const updateGroup = (gIndex, key, next) => onChange(groups.map((g, idx) => (idx === gIndex ? { ...g, [key]: next } : g)));
  const addGroup = () => onChange([...groups, { group: "New Group", items: [] }]);
  const removeGroup = (gIndex) => onChange(groups.filter((_, idx) => idx !== gIndex));
  const updateItem = (gIndex, iIndex, key, next) => {
    const items = (groups[gIndex].items || []).map((it, idx) => (idx === iIndex ? { ...it, [key]: next } : it));
    updateGroup(gIndex, "items", items);
  };
  const addItem = (gIndex) => updateGroup(gIndex, "items", [...(groups[gIndex].items || []), { label: "", value: "" }]);
  const removeItem = (gIndex, iIndex) => updateGroup(gIndex, "items", (groups[gIndex].items || []).filter((_, idx) => idx !== iIndex));
  return (
    <div className="md:col-span-2">
      <div className="mb-2 flex items-center justify-between">
        <Label>{label}</Label>
        <Button type="button" size="sm" variant="outline" onClick={addGroup} className="rounded-sm"><Plus className="mr-1 h-4 w-4" />Add group</Button>
      </div>
      <div className="grid gap-3">
        {groups.map((g, gIndex) => (
          <div key={gIndex} className="border border-slate-200 p-3">
            <div className="flex items-center gap-2">
              <Input placeholder="Group name (e.g. Camera, Lens, Power)" value={g.group || ""} onChange={(e) => updateGroup(gIndex, "group", e.target.value)} />
              <Button type="button" variant="outline" className="rounded-sm border-red-200 text-red-600" onClick={() => removeGroup(gIndex)}><Trash2 className="h-4 w-4" /></Button>
            </div>
            <div className="mt-2 grid gap-2">
              {(g.items || []).map((it, iIndex) => (
                <div key={iIndex} className="grid gap-2 md:grid-cols-[1fr_1fr_auto]">
                  <Input placeholder="Label (e.g. Resolution)" value={it.label || ""} onChange={(e) => updateItem(gIndex, iIndex, "label", e.target.value)} />
                  <Input placeholder="Value (leave blank if unverified)" value={it.value || ""} onChange={(e) => updateItem(gIndex, iIndex, "value", e.target.value)} />
                  <Button type="button" variant="outline" className="rounded-sm border-red-200 text-red-600" onClick={() => removeItem(gIndex, iIndex)}><Trash2 className="h-4 w-4" /></Button>
                </div>
              ))}
              <Button type="button" size="sm" variant="outline" className="rounded-sm" onClick={() => addItem(gIndex)}><Plus className="mr-1 h-4 w-4" />Add spec row</Button>
            </div>
          </div>
        ))}
        {!groups.length && <p className="text-xs text-slate-500">No specification groups yet. Leave blank values as "Pending verification" rather than guessing.</p>}
      </div>
    </div>
  );
}

function VariantsField({ label, columnsValue, columnsOnChange, value, onChange }) {
  const columns = Array.isArray(columnsValue) && columnsValue.length ? columnsValue : ["Model"];
  const rows = Array.isArray(value) ? value : [];
  const updateColumns = (text) => columnsOnChange(text.split(",").map((c) => c.trim()).filter(Boolean));
  const updateRow = (index, col, next) => onChange(rows.map((row, idx) => (idx === index ? { ...row, [col]: next } : row)));
  const addRow = () => onChange([...rows, {}]);
  const removeRow = (index) => onChange(rows.filter((_, idx) => idx !== index));
  return (
    <div className="md:col-span-2">
      <Label>{label}</Label>
      <p className="mt-1 text-xs text-slate-500">Comma-separated column names, e.g. Model, Resolution, Lens, IR Range, Warranty</p>
      <Input className="mt-2" value={columns.join(", ")} onChange={(e) => updateColumns(e.target.value)} />
      <div className="mt-3 grid gap-2">
        {rows.map((row, index) => (
          <div key={index} className="grid gap-2 border border-slate-200 p-3">
            <div className="grid gap-2" style={{ gridTemplateColumns: `repeat(${columns.length}, 1fr)` }}>
              {columns.map((col) => (
                <Input key={col} placeholder={col} value={row[col] || ""} onChange={(e) => updateRow(index, col, e.target.value)} />
              ))}
            </div>
            <div><Button type="button" size="sm" variant="outline" className="rounded-sm border-red-200 text-red-600" onClick={() => removeRow(index)}><Trash2 className="mr-1 h-4 w-4" />Remove model</Button></div>
          </div>
        ))}
        <Button type="button" size="sm" variant="outline" className="rounded-sm" onClick={addRow}><Plus className="mr-1 h-4 w-4" />Add model / variant</Button>
      </div>
    </div>
  );
}

function StatsEditor({ rows, onChange }) {
  const safeRows = Array.isArray(rows) ? rows : [];
  const update = (index, key, value) => onChange(safeRows.map((row, idx) => idx === index ? { ...row, [key]: value } : row));
  const add = () => onChange([...safeRows, { value: "100+", label: "New stat" }]);
  const remove = (index) => onChange(safeRows.filter((_, idx) => idx !== index));
  return (
    <div className="md:col-span-2">
      <div className="mb-2 flex items-center justify-between">
        <Label>Homepage statistics</Label>
        <Button type="button" size="sm" variant="outline" onClick={add} className="rounded-sm"><Plus className="mr-1 h-4 w-4" />Add stat</Button>
      </div>
      <div className="grid gap-2">
        {safeRows.map((row, index) => (
          <div key={index} className="grid gap-2 md:grid-cols-[1fr_2fr_auto]">
            <Input value={row.value || ""} onChange={(e) => update(index, "value", e.target.value)} />
            <Input value={row.label || ""} onChange={(e) => update(index, "label", e.target.value)} />
            <Button type="button" variant="outline" className="rounded-sm border-red-200 text-red-600" onClick={() => remove(index)}><Trash2 className="h-4 w-4" /></Button>
          </div>
        ))}
      </div>
    </div>
  );
}

function makeEmptyItem(tab, sort) {
  const base = { sort, active: true };
  if (tab === "faqs") return { ...base, question: "New question", answer: "", category: "General" };
  if (tab === "testimonials") return { ...base, name: "Client name", business: "", industry: "", review: "", rating: 5, image: "" };
  if (tab === "partners") return { ...base, name: "Partner name", logo: "", website: "" };
  if (tab === "downloads") return { ...base, title: "New download", category: "Brochure", url: "", summary: "", image: "" };
  const singular = tab.replace(/ies$/, "y").replace(/s$/, "");
  const title = `New ${singular}`;
  const productExtras = tab === "products" ? {
    subcategory: "", galleryImages: [], lifestyleImage: "", videoUrl: "", availabilityStatus: "Enquiry Based", badges: [],
    keyFeatures: [], specGroups: [], variants: [], variantColumns: [], applications: [], industriesServed: [],
    installationSteps: [], downloads: [], faqs: [], certifications: [], whatsappMessageTemplate: "",
    seoTitle: "", seoDescription: "", seoKeywords: "", canonicalUrl: "", robotsIndex: true,
  } : {};
  return { ...base, title, slug: slugify(title), category: "", summary: "", image: "", features: [], benefits: [], description: "", priceText: "", modelNumber: "", technicalSpecifications: [], warrantyInformation: "", brochureDownloadMode: "dealer_only", brochureTemplate: "single", ...productExtras };
}

function slugify(value) {
  return String(value || "item").toLowerCase().trim().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "") || "item";
}
